import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import {
  Accordion,
  Divider,
  Dropdown,
  Grid,
  Loader,
  Select,
} from 'semantic-ui-react';

import Fetcher from '../reusable/Fetcher';
import { localGet, localSet } from '../../utils/localStorage';
import LeftAlignedAccordionHeaderTitle from '../reusable/LeftAlignedAccordionHeaderTitle';
import TimelineChart from '../reusable/TimelineChart';
import MetricsTable from './MetricsTable';
import './style.css';

const selectedTimeKey = 'Metrics/SelectedTime';
const selectedMachinesKey = 'Metrics/SelectedMachines';

const periodOptions = ['hour', 'day', 'week', 'month'];

const getStartAndEndTime = (period) => {
  const endTime = Date.now();
  let startTime = endTime;

  switch (period) {
    case 'hour':
      startTime -= 60 * 60 * 1000;
      break;
    case 'day':
      startTime -= 24 * 60 * 60 * 1000;
      break;
    case 'week':
      startTime -= 24 * 60 * 60 * 1000 * 7;
      break;
    case 'month':
      startTime -= 24 * 60 * 60 * 1000 * 30;
      break;
    default:
      break;
  }

  return [startTime, endTime];
};

class Metrics extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    getOrganisationMachines: PropTypes.func.isRequired,
    machines: PropTypes.shape({
      isPending: PropTypes.bool.isRequired,
      allIds: PropTypes.arrayOf(PropTypes.string).isRequired,
      byId: PropTypes.shape({}).isRequired,
    }).isRequired,
    getTelemetry: PropTypes.func.isRequired,
    telemetry: PropTypes.shape({}).isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      metricsAccordionActive: true,
      timelineAccordionActive: true,
      period: localGet(selectedTimeKey, periodOptions[1]),
      selectedMachines: localGet(selectedMachinesKey, []),
    };
    this.updateTime = this.updateTime.bind(this);
    this.machineWasSelected = this.machineWasSelected.bind(this);
    this.handleMetricsAccordionClick = this.handleMetricsAccordionClick.bind(this);
    this.handleTimelineAccordionClick = this.handleTimelineAccordionClick.bind(this);
    this.renderSelectedMachines = this.renderSelectedMachines.bind(this);
  }

  componentWillReceiveProps(props) {
    if (this.props.machines === props.machines) {
      return;
    }
    const { allIds } = props.machines;

    this.setState(prevState => ({
      selectedMachines: prevState.selectedMachines.filter(machine => allIds.includes(machine.id)),
    }));
  }

  machineWasSelected(ev, data) {
    const { allIds, byId } = this.props.machines;
    const shortNameList = allIds.map(id => byId[id].shortName);

    const selectedMachines = data.value.map(shortName => ({
      shortName,
      id: allIds[shortNameList.indexOf(shortName)],
    }));

    // NOTE: changing selectedMachines state triggers render of the table, which fetches new data
    localSet(selectedMachinesKey, selectedMachines);
    this.setState({ selectedMachines });
  }

  handleMetricsAccordionClick() {
    this.setState(prevState => ({
      metricsAccordionActive: !prevState.metricsAccordionActive,
    }));
  }

  handleTimelineAccordionClick() {
    this.setState(prevState => ({
      timelineAccordionActive: !prevState.timelineAccordionActive,
    }));
  }

  updateTime(e, { value }) {
    const period = value;
    localSet(selectedTimeKey, period);
    this.setState({ period });

    // request new data from backend
    const [startTime] = getStartAndEndTime(period);
    const machineIds = this.state.selectedMachines.map(machine => machine.id);
    this.props.getTelemetry(machineIds, startTime);
  }

  renderMachineSelector() {
    return (
      <div>
        <img src="/images/historic-select.png" alt="select" />
      </div>
    );
  }

  renderSelectedMachines(selectedMachines) {
    const { t, getTelemetry, telemetry } = this.props;
    const { metricsAccordionActive, period, timelineAccordionActive } = this.state;
    const [startTime, endTime] = getStartAndEndTime(period);

    return (
      <Grid padded>
        <Grid.Row>
          <Accordion fluid>
            <LeftAlignedAccordionHeaderTitle
              active={metricsAccordionActive}
              onClick={this.handleMetricsAccordionClick}
              label={t('metrics.overview')}
            />
            <Accordion.Content active={metricsAccordionActive}>
              <MetricsTable
                getTelemetry={getTelemetry}
                telemetry={telemetry}
                machines={selectedMachines}
                startTime={startTime}
                endTime={endTime}
              />
            </Accordion.Content>
          </Accordion>
        </Grid.Row>
        <Grid.Row>
          <Accordion fluid>
            <LeftAlignedAccordionHeaderTitle
              active={timelineAccordionActive}
              onClick={this.handleTimelineAccordionClick}
              label={t('metrics.timeline')}
            />
            <Accordion.Content active={timelineAccordionActive}>
              {selectedMachines.map(({ shortName, id }, index) => {
                const machineData = telemetry[id];
                return (
                  <div style={{ paddingTop: `${index > 0 ? 12 : 0}px` }} key={shortName}>
                    <span style={{ float: 'left', fontWeight: 'bold' }}>
                      {shortName}
                    </span>
                    <Divider clearing />
                    {machineData && !machineData.isPending
                      ? (
                        <TimelineChart
                          machineId={id}
                          startTime={startTime}
                          endTime={endTime}
                          period={period}
                          values={machineData.allIds.map(idx => machineData.byId[idx])}
                        />)
                      : <Loader size="small" active inline="centered" className="fetcher-loader" />
                    }
                  </div>
                );
              })}
              <div style={{ height: '250px' }} />
            </Accordion.Content>
          </Accordion>
        </Grid.Row>
      </Grid>
    );
  }

  render() {
    const { t, getOrganisationMachines, machines: { isPending, allIds, byId } } = this.props;
    const { selectedMachines, period } = this.state;

    const shortNameList = [];
    const machineSelectorOptions = [];
    const machineSelectorValue = selectedMachines.map(machine => machine.shortName);

    allIds.forEach((id) => {
      const { shortName } = byId[id];

      shortNameList.push(shortName);
      machineSelectorOptions.push({
        text: shortName,
        value: shortName,
      });
    });

    const translatedPeriodOptions = periodOptions.map(key => ({
      key,
      value: key,
      text: t(`metrics.${key}`),
    }));

    return (
      <Fetcher
        fetchAction={getOrganisationMachines}
        isLoading={isPending}
      >
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <span>{t('metrics.intro')}</span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8} stretched>
              <Select
                multiple
                search
                options={machineSelectorOptions}
                placeholder={t('metrics.machine-select')}
                value={machineSelectorValue}
                onChange={this.machineWasSelected}
              />
            </Grid.Column>
            <Grid.Column width={8} stretched>
              <div className="time-select-historic-container">
                <span>{t('metrics.show-result')} {''}
                  <Dropdown
                    value={period}
                    options={translatedPeriodOptions}
                    pointing
                    inline
                    className="link item"
                    style={{ height: '20px' }}
                    onChange={this.updateTime}
                  />
                </span>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              {selectedMachines.length === 0
                ? this.renderMachineSelector()
                : this.renderSelectedMachines(selectedMachines)}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Fetcher>
    );
  }
}

export default translate()(Metrics);



// WEBPACK FOOTER //
// ./src/components/Metrics/Metrics.js